import React, { useEffect, useState } from 'react';
import { KassaService } from 'services/KassaService';
import { message, Table } from 'antd';
import { usePagination } from 'hooks/usePagination';
import { PAGE_SIZE_OPTIONS } from 'constants/table';
import { checkValue, formatDate } from 'utils';
import { useFilters } from 'hooks/useFilters';
import { priceRender } from 'utils/table';
import { SumRange } from 'components/Filters/SumRange';
import { FilterIcon } from 'components/Filters/FilterIcon';
import { RangePickerFilter } from 'components/Filters/RangePickerFilter';
import { InputFilter } from 'components/Filters/InputFilter';
import { ChecksTable } from '../ChecksTable';

function ShiftsTable({ kassaId, onRowClick }) {
  const [loading, setLoading] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [selectedShiftId, setSelectedShiftId] = useState(null);
  const [expandedKey, setExpandedKey] = useState([]);
  const { page, perPage, totalRows, setPagination, setTotalRows } =
    usePagination();
  const { filters, handleFilters } = useFilters({
    number: null,
    from_created_at: null,
    to_created_at: null,
    from_closed_at: null,
    to_closed_at: null,
    from_sells: undefined,
    to_sells: undefined,
    from_refunds: undefined,
    to_refunds: undefined,
  });

  const {
    number,
    from_created_at,
    to_created_at,
    from_closed_at,
    to_closed_at,
    from_sells,
    to_sells,
    from_refunds,
    to_refunds,
  } = filters;

  const columns = [
    {
      title: 'Смена',
      dataIndex: 'number',
      key: 'number',
      filterDropdown: () => (
        <InputFilter
          value={number}
          placeholder="Поиск по номеру"
          onFilterChange={(number) => handleFilters({ number })}
        />
      ),
      filterIcon: () => <FilterIcon search isActive={number} />,
    },
    {
      title: 'Открытие смены',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => {
        if (checkValue(date) === date)
          return formatDate(date, 'DD.MM.YYYY, HH.mm');
        return checkValue(date);
      },
      filterDropdown: () => {
        return (
          <RangePickerFilter
            value={[from_created_at, to_created_at]}
            onFilterChange={([start, end]) => {
              handleFilters({ from_created_at: start, to_created_at: end });
            }}
          />
        );
      },
      filterIcon: () => (
        <FilterIcon isActive={from_created_at && to_created_at} />
      ),
    },
    {
      title: 'Закрытие смены',
      dataIndex: 'closed_at',
      key: 'closed_at',
      render: (date) => {
        if (checkValue(date) === date)
          return formatDate(date, 'DD.MM.YYYY, HH.mm');
        return checkValue(date);
      },
      filterDropdown: () => {
        return (
          <RangePickerFilter
            value={[from_closed_at, to_closed_at]}
            onFilterChange={([start, end]) => {
              handleFilters({ from_closed_at: start, to_closed_at: end });
            }}
          />
        );
      },
      filterIcon: () => (
        <FilterIcon isActive={from_closed_at && to_closed_at} />
      ),
    },
    {
      title: 'Сумма продаж',
      dataIndex: 'sells',
      key: 'sells',
      render: priceRender,
      filterDropdown: () => {
        return (
          <SumRange
            fromValue={from_sells}
            toValue={to_sells}
            getRange={(first, second) =>
              handleFilters({ from_sells: first, to_sells: second })
            }
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={from_sells || to_sells} />,
    },
    {
      title: 'Сумма возвратов',
      dataIndex: 'refunds',
      key: 'refunds',
      render: priceRender,
      filterDropdown: () => {
        return (
          <SumRange
            fromValue={from_refunds}
            toValue={to_refunds}
            getRange={(first, second) =>
              handleFilters({ from_refunds: first, to_refunds: second })
            }
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={from_refunds || to_refunds} />,
    },
  ];

  useEffect(() => {
    fetchShifts();
  }, [kassaId, page, perPage, filters]);

  const fetchShifts = async () => {
    if (!kassaId || !page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await KassaService.getShifts(
        kassaId,
        page,
        perPage,
        filters
      );

      setTotalRows(total);
      setShifts(items);
    } catch (err) {
      message.error('Не удалось загрузить смены по этой кассе');
    } finally {
      setLoading(false);
    }
  };

  const renderChecksTable = (record) => {
    if (record?.id === selectedShiftId) {
      return <ChecksTable shiftId={selectedShiftId} onRowClick={onRowClick} />;
    }

    return null;
  };

  const handleExpand = (expanded, record) => {
    if (expanded) {
      setSelectedShiftId(record?.id);
      setExpandedKey([record.id]);
    } else {
      setExpandedKey([]);
    }
  };

  return (
    <Table
      rowKey="id"
      loading={loading}
      dataSource={shifts}
      columns={columns}
      rowClassName="test"
      pagination={{
        current: page,
        pageSize: perPage,
        total: totalRows,
        showSizeChanger: true,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
      }}
      expandable={{
        expandedRowRender: renderChecksTable,
        onExpand: handleExpand,
        expandedRowKeys: expandedKey,
      }}
      onRow={(record) => {
        return {
          onDoubleClick: () => onRowClick(record, 'shifts'),
        };
      }}
      onChange={setPagination}
    />
  );
}

export { ShiftsTable };
